// klm-admin-frontend/src/store/slices/maintenanceSlice.js
// Complete Redux Slice for Maintenance Feature

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/api/axios';

// ============================================================================
// API ENDPOINTS
// ============================================================================
const API_ROUTES = {
  CHECK_STATUS: '/admin/maintenance/status', // Changed from '/admin/maintenance'
  ACTIVATE: '/admin/maintenance/activate',
  DEACTIVATE: '/admin/maintenance/deactivate',
  HISTORY: '/admin/maintenance/history'
};

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Check current maintenance status
 */
export const checkMaintenanceStatus = createAsyncThunk(
  'maintenance/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ROUTES.CHECK_STATUS);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to check maintenance status'
      );
    }
  }
);

/**
 * Activate maintenance mode
 * @param {Object} data - { title, message, estimatedDuration, reason }
 */
export const activateMaintenance = createAsyncThunk(
  'maintenance/activate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ROUTES.ACTIVATE, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to activate maintenance mode'
      );
    }
  }
);

/**
 * Deactivate maintenance mode
 */
export const deactivateMaintenance = createAsyncThunk(
  'maintenance/deactivate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ROUTES.DEACTIVATE);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to deactivate maintenance mode'
      );
    }
  }
);

/**
 * Get maintenance history
 * @param {Object} params - { page, limit }
 */
export const getMaintenanceHistory = createAsyncThunk(
  'maintenance/getHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10 } = params;
      const response = await axiosInstance.get(
        `${API_ROUTES.HISTORY}?page=${page}&limit=${limit}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch maintenance history'
      );
    }
  }
);

// ============================================================================
// INITIAL STATE
// ============================================================================
const initialState = {
  // Current maintenance status
  isActive: false,
  title: '',
  message: '',
  estimatedDuration: 0,
  startedAt: null,
  endedAt: null,
  reason: '',

  // History
  history: {
    records: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0
    }
  },

  // UI States
  loading: false,
  historyLoading: false,
  error: null,
  success: null
};

// ============================================================================
// SLICE
// ============================================================================
const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    // Clear error message
    clearError: (state) => {
      state.error = null;
    },
    
    // Clear success message
    clearSuccess: (state) => {
      state.success = null;
    },
    
    // Reset entire state
    resetMaintenanceState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    // ========================================================================
    // CHECK MAINTENANCE STATUS
    // ========================================================================
    builder
      .addCase(checkMaintenanceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkMaintenanceStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.isActive = action.payload.isActive;
        state.title = action.payload.title || '';
        state.message = action.payload.message || '';
        state.estimatedDuration = action.payload.estimatedDuration || 0;
        state.startedAt = action.payload.startedAt || null;
        state.endedAt = action.payload.endedAt || null;
        state.reason = action.payload.reason || '';
      })
      .addCase(checkMaintenanceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // ACTIVATE MAINTENANCE
    // ========================================================================
    builder
      .addCase(activateMaintenance.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(activateMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.isActive = true;
        state.title = action.payload.title;
        state.message = action.payload.message;
        state.estimatedDuration = action.payload.estimatedDuration;
        state.startedAt = action.payload.startedAt;
        state.reason = action.payload.reason;
        state.success = 'Maintenance mode activated successfully';
      })
      .addCase(activateMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // DEACTIVATE MAINTENANCE
    // ========================================================================
    builder
      .addCase(deactivateMaintenance.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deactivateMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.isActive = false;
        state.endedAt = action.payload.endedAt;
        state.success = 'Maintenance mode deactivated successfully';
      })
      .addCase(deactivateMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ========================================================================
    // GET MAINTENANCE HISTORY
    // ========================================================================
    builder
      .addCase(getMaintenanceHistory.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(getMaintenanceHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history.records = action.payload.maintenanceRecords || [];
        state.history.pagination = action.payload.pagination || initialState.history.pagination;
      })
      .addCase(getMaintenanceHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload;
      });
  }
});

// ============================================================================
// EXPORTS
// ============================================================================

// Actions
export const { 
  clearError, 
  clearSuccess, 
  resetMaintenanceState 
} = maintenanceSlice.actions;

// Selectors
export const selectMaintenance = (state) => state.maintenance;
export const selectIsMaintenanceActive = (state) => state.maintenance.isActive;
export const selectMaintenanceLoading = (state) => state.maintenance.loading;
export const selectMaintenanceError = (state) => state.maintenance.error;
export const selectMaintenanceHistory = (state) => state.maintenance.history;

// Reducer
export default maintenanceSlice.reducer;